import { FC, useMemo, useState } from "react";
import { Plus, X, Search } from "lucide-react";
import { Input } from "src/components/ui/input";
import {
	PERMISSION_CATEGORIES,
	formatPermissionName
} from "../../constants/permissionCategories";

interface Props {
	availablePermissions: string[];
	selectedPermissions: string[];
	onChange: (selected: string[]) => void;
}

const PermissionTransferList: FC<Props> = ({
	availablePermissions,
	selectedPermissions,
	onChange
}) => {
	const [filter, setFilter] = useState("");

	const unselected = useMemo(
		() => availablePermissions.filter((p) => !selectedPermissions.includes(p)),
		[availablePermissions, selectedPermissions]
	);

	const addPermission = (permission: string) => {
		onChange([...selectedPermissions, permission]);
	};

	const removePermission = (permission: string) => {
		onChange(selectedPermissions.filter((p) => p !== permission));
	};

	const addCategory = (permissions: string[]) => {
		const toAdd = permissions.filter((p) => !selectedPermissions.includes(p));
		onChange([...selectedPermissions, ...toAdd]);
	};

	const removeCategory = (permissions: string[]) => {
		const toRemove = new Set(permissions);
		onChange(selectedPermissions.filter((p) => !toRemove.has(p)));
	};

	const filterLower = filter.toLowerCase();

	const availableCategories = PERMISSION_CATEGORIES.map((cat) => {
		const unselectedInCategory = cat.permissions.filter((p) =>
			unselected.includes(p)
		);
		const filtered = unselectedInCategory.filter(
			(p) =>
				!filter || formatPermissionName(p).toLowerCase().includes(filterLower)
		);
		return {
			...cat,
			permissions: filtered,
			allPermissions: unselectedInCategory
		};
	}).filter((cat) => cat.permissions.length > 0);

	const selectedCategories = PERMISSION_CATEGORIES.map((cat) => ({
		...cat,
		permissions: cat.permissions.filter((p) => selectedPermissions.includes(p))
	})).filter((cat) => cat.permissions.length > 0);

	return (
		<div className="flex gap-4">
			{/* Left panel — available */}
			<div className="flex-1 border border-border rounded-lg bg-white overflow-hidden">
				<div className="bg-zinc-50 px-4 py-3 border-b border-border flex items-center justify-between">
					<span className="text-sm font-semibold text-foreground">
						Available Permissions
					</span>
					<span className="text-sm text-muted-foreground">
						({unselected.length})
					</span>
				</div>
				<div className="p-4">
					<div className="relative mb-3">
						<Search
							className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground"
							aria-hidden="true"
						/>
						<Input
							aria-label="Filter permissions"
							placeholder="Filter..."
							className="pl-9 h-9 text-sm bg-white"
							value={filter}
							onChange={(e) => setFilter(e.target.value)}
						/>
					</div>
					<div className="max-h-[400px] overflow-y-auto pr-2">
						{availableCategories.length === 0 && (
							<p className="text-sm text-muted-foreground text-center py-6">
								{filter
									? "No matching permissions"
									: "All permissions assigned"}
							</p>
						)}
						{availableCategories.map((cat) => (
							<div key={cat.label} className="mb-3">
								<div className="flex items-center justify-between px-2 py-1.5">
									<span className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
										{cat.label}
									</span>
									<button
										type="button"
										className="text-xs text-primary hover:text-primary/80 transition-colors"
										onClick={() => addCategory(cat.allPermissions)}
									>
										add all →
									</button>
								</div>
								{cat.permissions.map((permission) => (
									<button
										key={permission}
										type="button"
										className="flex items-center gap-3 w-full px-3 py-2 text-sm text-foreground hover:bg-zinc-50 rounded-md transition-colors"
										onClick={() => addPermission(permission)}
									>
										<Plus className="h-4 w-4 text-primary flex-shrink-0" />
										<span>{formatPermissionName(permission)}</span>
									</button>
								))}
							</div>
						))}
					</div>
				</div>
			</div>

			{/* Right panel — selected */}
			<div className="flex-1 border border-border rounded-lg bg-white overflow-hidden">
				<div className="bg-green-50 px-4 py-3 border-b border-green-200 flex items-center justify-between">
					<span className="text-sm font-semibold text-foreground">
						Selected Permissions
					</span>
					<span className="text-sm text-green-700">
						({selectedPermissions.length})
					</span>
				</div>
				<div className="p-4">
					<div className="max-h-[400px] overflow-y-auto pr-2">
						{selectedCategories.length === 0 && (
							<p className="text-sm text-muted-foreground text-center py-6">
								No permissions selected
							</p>
						)}
						{selectedCategories.map((cat) => (
							<div key={cat.label} className="mb-3">
								<div className="flex items-center justify-between px-2 py-1.5">
									<span className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
										{cat.label}
									</span>
									<button
										type="button"
										className="text-xs text-red-500 hover:text-red-400 transition-colors"
										onClick={() => removeCategory(cat.permissions)}
									>
										← remove all
									</button>
								</div>
								{cat.permissions.map((permission) => (
									<button
										key={permission}
										type="button"
										className="flex items-center gap-3 w-full px-3 py-2 text-sm text-foreground hover:bg-zinc-50 rounded-md transition-colors"
										onClick={() => removePermission(permission)}
									>
										<X className="h-4 w-4 text-red-500 flex-shrink-0" />
										<span>{formatPermissionName(permission)}</span>
									</button>
								))}
							</div>
						))}
					</div>
				</div>
			</div>
		</div>
	);
};

export default PermissionTransferList;
